import React, { FC, lazy, Suspense, useEffect, useRef } from 'react';
import styled, { css } from 'styled-components';
import { HelperTextProps, Uds } from '@utmost.co/utmost-design-system';
import { FormHelperText, FormLabel, Loading } from '@beeline.com/bp-design-system';
import { type IAceEditorProps } from 'react-ace';
import { type Ace } from 'ace-builds';

const ReactAceEditor = lazy(() => import('./NativeReactAce'));
export type AceEditorProps = Uds<
  IAceEditorProps & {
    actions?: JSX.Element[] | JSX.Element;
    error?: boolean;
    label?: string;
    /* @default: json */
    mode?: 'json' | 'mysql' | 'yaml' | 'text' | 'xml';
    theme?: 'textmate';
    helperTextProps?: HelperTextProps;
    required?: boolean;
    disabled?: boolean;
    helperText?: string;
    errorMessage?: string;
    hightlightLine?: number;
  }
>;

export const AceEditor: FC<React.PropsWithChildren<AceEditorProps>> = ({
  required,
  label,
  errorMessage,
  error,
  actions,
  dataTest,
  helperText,
  helperTextProps,
  setOptions,
  readOnly,
  disabled,
  enableBasicAutocompletion = true,
  enableLiveAutocompletion = true,
  showPrintMargin = false,
  minLines = 10,
  mode = 'json',
  theme = 'textmate',
  width = '100%',
  hightlightLine,
  ...props
}) => {
  const isBlocked = !!(readOnly || disabled);
  const aceEditor = useRef<any>();

  useEffect(() => {
    // Highlights a line if the property is set
    if (aceEditor.current && hightlightLine !== undefined) {
      const editor = aceEditor.current.editor as Ace.Editor;
      editor.gotoLine(hightlightLine + 1, 0, true);
      editor.selection.moveCursorToPosition({ row: hightlightLine, column: 0 });
      editor.selection.selectLine();
    }
  }, [hightlightLine]);

  return (
    <div data-test={dataTest ?? 'ace-editor'} style={{ width }}>
      {!!label && (
        <FormLabel style={actions ? { marginBottom: 0 } : undefined} error={error} shrink required={required}>
          {label}
        </FormLabel>
      )}
      <StyledHeader>
        {error && <FormLabel error>{errorMessage}</FormLabel>}
        {actions && <StyledActions $hasError={!!error}>{actions}</StyledActions>}
      </StyledHeader>
      <StyledEditorWrapper $readOnly={isBlocked} $error={!!error} $width={width}>
        <Suspense fallback={<Loading />}>
          <ReactAceEditor
            ref={aceEditor}
            mode={mode}
            theme={theme}
            width={width}
            readOnly={isBlocked}
            enableBasicAutocompletion={enableBasicAutocompletion}
            enableLiveAutocompletion={enableLiveAutocompletion}
            showPrintMargin={showPrintMargin}
            minLines={minLines}
            setOptions={
              setOptions || {
                useWorker: false,
                tabSize: 2,
                wrap: true,
                showLineNumbers: true
              }
            }
            {...props}
          />
        </Suspense>
      </StyledEditorWrapper>

      {!!helperText && (
        <FormHelperText error={error} {...helperTextProps}>
          {helperText}
        </FormHelperText>
      )}
    </div>
  );
};

const StyledActions = styled.div<{ $hasError: boolean }>`
  display: flex;
  ${({ $hasError }) => (!$hasError ? 'flex-basis: 100%' : '')};
  justify-content: flex-end;
`;

const StyledHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const readOnlyStyles = css<{ $readOnly: boolean }>`
  border-color: ${({ theme }) => theme.stateOnSurfaceDisabledContainerStateLayerColor};
  .ace_content {
    background: ${({ theme }) => theme.stateOnSurfaceDisabledContainerStateLayerColor};
    color: ${({ theme }) => theme.stateOnSurfaceDisabledContentStateLayerColor};
  }
  .ace_cursor {
    opacity: 0;
  }
  &:focus-within {
    border-color: ${({ theme }) => theme.stateOnSurfaceDisabledContainerStateLayerColor};
  }
`;

const errorStyles = css`
  border-color: ${({ theme }) => theme.colorAlert};
  &:focus-within {
    border-color: ${({ theme }) => theme.colorAlert};
  }
`;

const StyledEditorWrapper = styled.div<{ $readOnly: boolean; $error: boolean; $width: string }>`
  .ace_editor,
  .ace_editor div,
  .ace_editor .ace_line * {
    font-size: ${({ theme }) => theme.font.sizeSmall} !important;
    font-variant-ligatures: none !important;
    font-family: 'Monaco', 'Menlo', 'Ubuntu Mono', 'Droid Sans Mono', 'Consolas', monospace !important;
    font-weight: 400 !important;
    letter-spacing: 0 !important;
  }

  width: ${({ $width }) => $width};
  box-sizing: content-box;
  border: 1px solid ${({ theme }) => theme.colorSurfaceVariant};
  border-radius: 2px;

  &:focus-within {
    border-color: ${({ theme }) => theme.colorPrimary};
  }
  ${({ $readOnly }) => ($readOnly ? readOnlyStyles : '')}
  ${({ $error }) => ($error ? errorStyles : '')}
`;
